/**
 * ATS POST /api/ai-calls/initiate body — Indus click2call contract only.
 * ATS must forward this JSON as-is (not remap to to / agent_id).
 */

import {
  resolveInitiateTranscriptLanguage,
  normalizeTranscriptLanguage,
  TEST_CALL_DEMO_CANDIDATE_NAME,
} from '@/utils/ai-calling-prompt-defaults'
import {
  AiCallingConfigError,
  assertInitiateReady,
} from '@/utils/ai-calling-config-schema'

/** Production ATS Indus post-call webhook (not example.com). */
export const ATS_INDUS_AI_CALL_CALLBACK_URL = 'https://ats.sattvahuman.in/webhooks/indus/ai-call'

const INDUS_INITIATE_KEYS = [
  'customer_number',
  'agent_number',
  'callback_url',
  'transcript',
  'transcript_language',
  'agent_config',
]

/** Normalize phone for Indus click2call (mirrors Python test_ai_agent.normalize_phone). */
export function normalizePhoneForIndus(phone) {
  const digits = String(phone || '').replace(/\D/g, '')
  if (digits.length === 10) return `91${digits}`
  if (digits.length >= 12 && digits.startsWith('91')) return digits.slice(0, 12)
  return digits
}

/** Bound Indus/ATS agent id. Never rewrite AGT_ → ACT_. Never treat empty as a wipe. */
export function extractBoundAgentId(record, fallback = '') {
  const src = record && typeof record === 'object' ? record : {}
  const nested = src.data && typeof src.data === 'object' && !Array.isArray(src.data)
    ? src.data
    : {}
  const candidates = [
    src.agent_id,
    src.agentId,
    src.agent_number,
    src.reuse_agent_id,
    src.indus_agent_id,
    src.voice_agent_id,
    nested.agent_id,
    nested.agentId,
    nested.agent_number,
    nested.reuse_agent_id,
    nested.indus_agent_id,
    nested.voice_agent_id,
  ]
  for (let i = 0; i < candidates.length; i += 1) {
    const id = String(candidates[i] || '').trim()
    if (id) return id
  }
  return String(fallback || '').trim()
}

export function coerceRequiredInitiateId(value, fallback) {
  const n = Number(value)
  if (Number.isFinite(n) && n > 0) return n
  const fb = Number(fallback)
  if (Number.isFinite(fb) && fb > 0) return fb
  return value
}

function firstNonEmpty(...values) {
  for (let i = 0; i < values.length; i += 1) {
    const text = String(values[i] == null ? '' : values[i]).trim()
    if (text) return text
  }
  return ''
}

function asObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {}
}

/** Public ATS webhook. Never use localhost / example.com. */
export function resolveInitiateCallbackUrl(input = {}) {
  const src = asObject(input)
  const explicit = firstNonEmpty(src.callback_url, src.callbackUrl)
  if (explicit) return explicit
  let envUrl = ''
  try {
    envUrl = firstNonEmpty(
      typeof process !== 'undefined' && process.env && process.env.VUE_APP_INDUS_CALLBACK_URL,
    )
  } catch (e) {
    envUrl = ''
  }
  if (envUrl && !/localhost|127\.0\.0\.1|example\.com/i.test(envUrl)) return envUrl
  return ATS_INDUS_AI_CALL_CALLBACK_URL
}

function resolveInitiateDisplayNames(input = {}) {
  const src = asObject(input)
  const rawConfig = asObject(src.agent_config)
  const infields = asObject(src.call_infields || src.callInfields || src.infields || rawConfig)
  const candidateName = firstNonEmpty(
    src.candidate_name,
    src.candidateName,
    infields.candidate_name,
    rawConfig.candidate_name,
    TEST_CALL_DEMO_CANDIDATE_NAME,
  )
  const userName = firstNonEmpty(
    src.user_name,
    src.userName,
    src.recruiter_name,
    src.recruiterName,
    rawConfig.user_name,
    infields.user_name,
    candidateName,
  )
  return {
    user_name: userName,
    candidate_name: candidateName,
  }
}

function resolveInitiateTranscriptLanguageOrEn(input = {}) {
  const explicitLang = normalizeTranscriptLanguage(
    input.transcript_language || input.transcriptLanguage,
  )
  const resolved = explicitLang === 'multi'
    ? ''
    : (explicitLang || resolveInitiateTranscriptLanguage(input))
  return resolved || 'en'
}

/**
 * Indus click2call body for POST /api/ai-calls/initiate.
 * Only the keys Indus listed — extra ATS fields caused 502 when remapped.
 */
export function buildInitiateCallPayload(input = {}) {
  const src = asObject(input)
  const customerNumber = normalizePhoneForIndus(
    src.customer_number || src.customerNumber || src.contact_no || src.contactNo,
  )
  const agentNumber = extractBoundAgentId(src)
  const callbackUrl = resolveInitiateCallbackUrl(src)
  const transcriptLanguage = resolveInitiateTranscriptLanguageOrEn(src)
  const names = resolveInitiateDisplayNames(src)

  const payload = {
    customer_number: customerNumber,
    agent_number: agentNumber,
    callback_url: callbackUrl,
    transcript: src.transcript !== false,
    transcript_language: transcriptLanguage,
    agent_config: {
      user_name: names.user_name,
      candidate_name: names.candidate_name,
    },
  }

  try {
    assertInitiateReady(src, {
      ...payload,
      agent_id: payload.agent_number,
      contact_no: payload.customer_number,
    })
  } catch (err) {
    if (err instanceof AiCallingConfigError) throw err
    throw err
  }

  const extra = Object.keys(payload).filter(key => !INDUS_INITIATE_KEYS.includes(key))
  extra.forEach(key => delete payload[key])
  return payload
}
